import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from './useApi';
import { useAuth } from '../contexts/AuthContext';

// Define a default status for better initialization and type safety
const DEFAULT_STATUS = {
    onboardingComplete: false,
    currentStep: 0,
    nextStep: 'profile',
    user: null,
    gym: null,
    branches: [],
};

interface OnboardingStatus {
    onboardingComplete: boolean;
    currentStep: number;
    nextStep: string;
    user: any;
    gym: any;
    branches: any[];
}
interface ProfileData {
    firstName: string;
    lastName: string;
    phoneNumber: string;
}
interface GymData {
    gymName: string;
    gymDisplayName?: string;
    gymEmail?: string;
    gymPhoneNumbers?: string[];
    gymGstin?: string;
    gymAddress?: string;
}
interface BranchData {
    branchName: string;
    branchDisplayName?: string;
    branchAddress?: string;
    branchCity?: string;
    branchPincode?: string;
    branchPhoneNumbers?: string[];
    branchEmail?: string;
    branchGstin?: string;
}


export const useOnboarding = () => {
    const { fetchWithAuth } = useApi();
    const { loading: authLoading, user, token } = useAuth();
    const location = useLocation();
    const isSaaS = location.pathname.startsWith('/saas');

    // Try to load onboarding completion status from localStorage
    const getStoredCompletionStatus = () => {
        try {
            const stored = localStorage.getItem('onboarding_complete');
            return stored === 'true';
        } catch {
            return false;
        }
    };

    // Initialize status based on localStorage
    const initialStatus = getStoredCompletionStatus()
        ? { ...DEFAULT_STATUS, onboardingComplete: true }
        : DEFAULT_STATUS;

    // Initialize status with a non-null default object
    const [status, setStatus] = useState<OnboardingStatus>(initialStatus);
    const [loading, setLoading] = useState(!getStoredCompletionStatus()); // Don't show loading if we know it's complete
    const [submitting, setSubmitting] = useState(false);
    // This state is crucial for preventing unnecessary network calls
    // Initialize from localStorage if available
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(getStoredCompletionStatus());

    // Add error and retry state
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;

    // Get onboarding status - wrapped in useCallback for dependency array stability
    const fetchStatus = useCallback(async (currentRetry = 0): Promise<any> => {
        // Don't fetch if auth is still loading or we're on a SaaS route
        if (authLoading || isSaaS) {
            if (isSaaS) setLoading(false);
            return;
        }

        if (!user || !token) {
            setLoading(false);
            return;
        }

        // Don't fetch if we already know onboarding is complete
        if (hasCompletedOnboarding) {
            setLoading(false);
            setFetchError(null);
            return;
        }

        // Only show blocking loading if we don't have a status yet or it's not complete
        if (!status.onboardingComplete) {
            setLoading(true);
        }

        try {
            // Clear any previous errors
            setFetchError(null);

            const response = await fetchWithAuth('/api/onboarding/status');
            setStatus(response);
            setRetryCount(0); // Reset retry count on success

            // If onboarding is complete, mark it so we don't fetch again
            if (response.onboardingComplete) {
                setHasCompletedOnboarding(true);
                // Persist to localStorage to survive page refreshes
                try {
                    localStorage.setItem('onboarding_complete', 'true');
                } catch (e) {
                    console.warn('Failed to save onboarding status to localStorage:', e);
                }
            }
            return response; // Return the new status for immediate use
        } catch (error) {
            console.error(`❌ Failed to fetch onboarding status (attempt ${currentRetry + 1}/${MAX_RETRIES}):`, error);

            // If we have retries left, try again with exponential backoff
            if (currentRetry < MAX_RETRIES) {
                const delay = Math.min(1000 * Math.pow(2, currentRetry), 5000); // Max 5 seconds
                console.log(`⏳ Retrying in ${delay}ms...`);

                await new Promise(resolve => setTimeout(resolve, delay));
                setRetryCount(currentRetry + 1);
                return fetchStatus(currentRetry + 1);
            }

            // Max retries exceeded - set error state
            setFetchError('Unable to verify account setup. Please check your connection and try again.');
            setRetryCount(0);

            // IMPORTANT: Don't redirect to onboarding on network errors
            // Only keep existing completed status or stay in loading/error state
            if (status.onboardingComplete) {
                // User was previously verified as complete - trust that
                console.log('✅ Using cached onboarding complete status');
            } else {
                // We don't know the status - DON'T assume incomplete
                // Keep the user on current page and show error
                console.warn('⚠️ Cannot determine onboarding status - showing error instead of redirecting');
            }
        } finally {
            setLoading(false);
        }
    }, [authLoading, user, token, hasCompletedOnboarding, status.onboardingComplete, fetchWithAuth, isSaaS, MAX_RETRIES]);

    // Submit profile data
    const submitProfile = async (data: ProfileData) => {
        try {
            setSubmitting(true);
            const response = await fetchWithAuth('/api/onboarding/profile', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            await fetchStatus(); // Refresh status
            return response;
        } catch (error) {
            console.error('❌ Failed to submit profile:', error);
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    // Submit gym data
    const submitGym = async (data: GymData) => {
        try {
            setSubmitting(true);
            const response = await fetchWithAuth('/api/onboarding/gym', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            await fetchStatus(); // Refresh status
            return response;
        } catch (error) {
            console.error('❌ Failed to submit gym:', error);
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    // Submit branch data
    const submitBranch = async (data: BranchData) => {
        try {
            setSubmitting(true);
            const response = await fetchWithAuth('/api/onboarding/branch', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            await fetchStatus(); // Refresh status
            return response;
        } catch (error) {
            console.error('❌ Failed to submit branch:', error);
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    // Complete onboarding in one step
    const completeOnboarding = async (data: ProfileData & GymData & BranchData) => {
        try {
            if (authLoading) {
                throw new Error('Authentication is still loading. Please wait.');
            }
            if (!user || !token) {
                throw new Error('Not authenticated. Please refresh the page and try again.');
            }

            setSubmitting(true);
            const response = await fetchWithAuth('/api/onboarding/complete', {
                method: 'POST',
                body: JSON.stringify(data)
            });

            // Force refresh status and capture the new status object
            const newStatus = await fetchStatus();

            // FIX: If the call succeeds and fetchStatus confirms completion,
            // the component consuming this hook will see 'isComplete' become true,
            // triggering the necessary redirection in the parent component.
            if (newStatus && newStatus.onboardingComplete) {
                setHasCompletedOnboarding(true);
            }

            return response;
        } catch (error) {
            console.error('❌ Failed to complete onboarding:', error);
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    // Load status on mount and when auth changes
    useEffect(() => {
        // Only fetch when auth is ready and we haven't completed onboarding
        if (!authLoading && !hasCompletedOnboarding) {
            fetchStatus();
        } else if (!authLoading && hasCompletedOnboarding) {
            // If we know onboarding is complete, just set loading to false
            setLoading(false);
        }
    }, [authLoading, hasCompletedOnboarding, fetchStatus]); // Include fetchStatus in dependency array

    return {
        status,
        loading,
        submitting,
        fetchStatus,
        submitProfile,
        submitGym,
        submitBranch,
        completeOnboarding,
        validateGstin: async (gstin: string) => {
            const response = await fetchWithAuth(`/api/onboarding/validate-gstin?gstin=${gstin}`);
            return response;
        },

        // Error and retry state
        error: fetchError,
        retryCount,

        // Helper getters
        // Check status directly since it is guaranteed to be an OnboardingStatus object or DEFAULT_STATUS
        isComplete: status.onboardingComplete,
        currentStep: status.currentStep,
        nextStep: status.nextStep,
        needsProfile: status.nextStep === 'profile',
        needsGym: status.nextStep === 'gym',
        needsBranch: status.nextStep === 'branch',
    };
};
